import { useSyncExternalStore } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NotificationSeverity = 'success' | 'error' | 'warning' | 'info';

export interface ActionCenterNotification {
  id: string;
  title: string;
  description?: string;
  severity: NotificationSeverity;
  timestamp: string;
  /** Optional entity link for navigation */
  entityType?: string;
  entityId?: string;
  /** Whether the user has seen this notification */
  read: boolean;
}

// ---------------------------------------------------------------------------
// localStorage-backed store (shared across components)
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'nexo:action-center:notifications';
const MAX_NOTIFICATIONS = 100;

let listeners: Array<() => void> = [];
let cachedNotifications: ActionCenterNotification[] | null = null;

function getNotifications(): ActionCenterNotification[] {
  if (cachedNotifications !== null) return cachedNotifications;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    cachedNotifications = raw ? JSON.parse(raw) : [];
  } catch {
    cachedNotifications = [];
  }
  return cachedNotifications!;
}

function persist(next: ActionCenterNotification[]) {
  cachedNotifications = next;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function getSnapshot(): ActionCenterNotification[] {
  return getNotifications();
}

// ---------------------------------------------------------------------------
// Public API (can be called outside React too, e.g. from SignalR callbacks)
// ---------------------------------------------------------------------------

let idCounter = 0;

export function addNotification(
  notification: Omit<ActionCenterNotification, 'id' | 'timestamp' | 'read'>,
): string {
  const id = `notif-${Date.now()}-${++idCounter}`;
  const entry: ActionCenterNotification = {
    ...notification,
    id,
    timestamp: new Date().toISOString(),
    read: false,
  };
  const current = getNotifications();
  persist([entry, ...current].slice(0, MAX_NOTIFICATIONS));
  return id;
}

export function dismissNotification(id: string) {
  persist(getNotifications().filter((n) => n.id !== id));
}

export function markAsRead(id: string) {
  persist(
    getNotifications().map((n) => (n.id === id ? { ...n, read: true } : n)),
  );
}

export function markAllAsRead() {
  persist(getNotifications().map((n) => ({ ...n, read: true })));
}

export function clearAllNotifications() {
  persist([]);
}

// ---------------------------------------------------------------------------
// Dev: expose on window for console testing
// ---------------------------------------------------------------------------

if (import.meta.env.DEV) {
  (window as any).__actionCenter = {
    addNotification,
    dismissNotification,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
  };
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export function useActionCenter() {
  const notifications = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    notifications,
    unreadCount,
    addNotification,
    dismissNotification,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
  };
}
